import ApiServiceNode from '../service/ApiServiceNode';
import logger from '../utils/logger';



export const roleService = {
    /**
     * List roles with filters
     * @param {Object} params - { page, limit, search, sortBy, sortOrder, departmentId }
     */
    async getRoles({ page = 1, limit = 20, search = '', sortBy = 'name', sortOrder = 'asc', departmentId = null }) {
        try {
            const params = { page, limit, search, sortBy, sortOrder };
            if (departmentId) {
                params.departmentId = departmentId;
            }

            const response = await ApiServiceNode.get('/settings/roles', { params });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching roles:', error);
            throw error;
        }
    },

    /**
     * Get departments the current user can manage for billable rate cards.
     */
    async getManagedDepartments() {
        try {
            const response = await ApiServiceNode.get('/settings/roles/departments/managed');
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching managed role departments:', error);
            throw error;
        }
    },

    /**
     * Get team members in a department for role mapping.
     * @param {number} departmentId - Department ID
     */
    async getDepartmentTeamMembers(departmentId, options = {}) {
        try {
            const params = {};
            if (options.includeInactive) {
                params.includeInactive = true;
            }

            const response = await ApiServiceNode.get(
                `/settings/roles/departments/${departmentId}/team-members`,
                { params }
            );
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching department team members:', error);
            throw error;
        }
    },

    /**
     * Get employees not mapped to a billable role by department.
     * @param {number|null} departmentId - Optional department ID
     */
    async getDepartmentCoverage(departmentId = null) {
        try {
            const params = {};
            if (departmentId) {
                params.departmentId = departmentId;
            }

            const response = await ApiServiceNode.get('/settings/roles/departments/coverage', { params });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching role coverage:', error);
            throw error;
        }
    },

    /**
     * Get single role with full details including rate history
     * @param {number} id - Role ID
     */
    async getRole(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/roles/${id}`);
            return response.data.data?.role || response.data.role || response.data;
        } catch (error) {
            logger.error('Error fetching role:', error);
            throw error;
        }
    },

    /**
     * Create new role with initial rate
     * @param {Object} data - { name, department_id, rate, valid_at? }
     */
    async createRole(data) {
        try {
            const response = await ApiServiceNode.post('/settings/roles', data);
            return response.data.data?.role || response.data.role || response.data;
        } catch (error) {
            logger.error('Error creating role:', error);
            throw error;
        }
    },

    /**
     * Update role (name and/or department only)
     * @param {number} id - Role ID
     * @param {Object} data - { name?, department_id? }
     */
    async updateRole(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/roles/${id}`, data);
            return response.data.data?.role || response.data.role || response.data;
        } catch (error) {
            logger.error('Error updating role:', error);
            throw error;
        }
    },

    /**
     * Replace the team members mapped to a role.
     * @param {number} id - Role ID
     * @param {number[]} userIds - Team member user IDs
     */
    async updateRoleTeamMembers(id, userIds = []) {
        try {
            const response = await ApiServiceNode.put(`/settings/roles/${id}/team-members`, {
                user_ids: userIds
            });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating role team members:', error);
            throw error;
        }
    },

    /**
     * Delete role (only if no users assigned)
     * @param {number} id - Role ID
     */
    async deleteRole(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/roles/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting role:', error);
            throw error;
        }
    },

    /**
     * Get rate history for a role
     * @param {number} id - Role ID
     */
    async getRateHistory(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/roles/${id}/rates`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching rate history:', error);
            throw error;
        }
    },

    /**
     * Get recent changes for a role on the billable rate card.
     * @param {number} id - Role ID
     */
    async getRoleHistory(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/roles/${id}/history`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching role history:', error);
            throw error;
        }
    },

    /**
     * Add new rate for a role (creates new rate entry with effective date)
     * @param {number} id - Role ID
     * @param {Object} data - { rate, valid_at }
     */
    async addRate(id, data) {
        try {
            const response = await ApiServiceNode.post(`/settings/roles/${id}/rates`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error adding rate:', error);
            throw error;
        }
    },

    /**
     * Delete a rate entry from rate history
     * @param {number} roleId - Role ID
     * @param {number} rateId - Rate ID
     */
    async deleteRate(roleId, rateId) {
        try {
            const response = await ApiServiceNode.delete(`/settings/roles/${roleId}/rates/${rateId}`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error deleting rate:', error);
            throw error;
        }
    },

    /**
     * Get users assigned to a role
     * @param {number} id - Role ID
     */
    async getUsersByRole(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/roles/${id}/users`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching users by role:', error);
            throw error;
        }
    }
};
